import type { SetupChecklistState } from '@/api/setup';
import { integrationAllowed } from './entitlements';

/**
 * One door for setting up a site: the /build wizard.
 *
 * "Build me a site" and "set up my site for me" in the chat, the dashboard's "Set up your site",
 * and the setup checklist's links all open the wizard, at the step that still needs doing. The
 * chat used to run its own interview and its own setup agent beside the wizard, which left
 * agents with three setups that each did part of the job (field report, 2026-10-04).
 *
 * Every way into setup, in one place. All of them land on /build, through setupWizardPath() or a
 * plain link to /build:
 *   - the chat: "build me a site", "set up my site", the agent's scaffold_site / setup_site tools
 *     (hooks/useStudioPage openSetupWizard)
 *   - the dashboard: "Set up your site", "Resume setup", the setup checklist card, and on a new site
 *     "Not sure what to type?"
 *   - the setup checklist's links in the chat
 *   - the Templates page's "Change Template" (the Design step)
 *   - My Sites "Set up", the MLS wizard's return link
 *
 * Retired setup screens (2026-10-05, Sites#788). Their URLs still work and redirect (App.tsx), so a
 * saved link or an old email lands in the wizard, which resumes where the agent left off:
 *   /onboarding, /setup, /wizard → /build;  /template-wizard → /build?step=looks.
 * Query strings on those old URLs are dropped on purpose: their state (a template step, a plan
 * pick) means nothing to /build, which reads the site's real state instead.
 *
 * BLT is the second way in (task 17, Dawn chose "Both" on 2026-10-19): "set up my site" in the
 * BLT chat walks these same steps there (api-gateway lib/setup-plan.ts, breadlist_plan_setup), each
 * through the same server writer the wizard's step calls, so the two cannot disagree. The Studio
 * chat above (the classic one beside BLT) still opens the wizard.
 *
 * Pure apart from the hand-off marker, which uses sessionStorage.
 */

export type WizardStepId = 'site' | 'brand' | 'looks' | 'pages' | 'content' | 'integrations' | 'legal' | 'finish';

export const WIZARD_STEP_IDS: readonly WizardStepId[] = [
  'site', 'brand', 'looks', 'pages', 'content', 'integrations', 'legal', 'finish',
];

export function isWizardStepId(v: unknown): v is WizardStepId {
  return typeof v === 'string' && (WIZARD_STEP_IDS as readonly string[]).includes(v);
}

/** The wizard URL for a step, optionally carrying the agent's description of their site (it
 *  pre-fills the Design step's "Make it custom with AI" box) and the areas they serve (they
 *  pre-fill the Content step's AI writer). */
export function setupWizardPath(opts: { step?: WizardStepId; prompt?: string; areas?: string[]; siteId?: string | null } = {}): string {
  const q = new URLSearchParams();
  if (opts.siteId) q.set('site', opts.siteId);
  if (opts.step) q.set('step', opts.step);
  const prompt = opts.prompt?.trim();
  if (prompt) q.set('prompt', prompt);
  const areas = (opts.areas ?? []).map((a) => a.trim()).filter(Boolean);
  if (areas.length) q.set('areas', areas.join(', '));
  const s = q.toString();
  return s ? `/build?${s}` : '/build';
}

/** Which steps the site has really finished, from its data rather than from what was clicked. */
export function wizardStepsDone(s: Pick<SetupChecklistState, 'hasBrand' | 'hasDesign' | 'mlsConnected' | 'legalPublished'>): Partial<Record<WizardStepId, boolean>> {
  return {
    brand: s.hasBrand,
    looks: s.hasDesign,
    integrations: s.mlsConnected,
    legal: s.legalPublished === true,
  };
}

/** The first step that still needs the agent: brand, then a look, then the content the AI can
 *  write, then the MLS (when the plan has listings), then the legal pages. All done → Finish. */
export function firstOpenWizardStep(s: SetupChecklistState): WizardStepId {
  if (!s.hasBrand) return 'brand';
  if (!s.hasDesign) return 'looks';
  if (!s.hasBio) return 'content';
  if (!s.mlsConnected && integrationAllowed(s.plan, 'mls')) return 'integrations';
  if (s.legalPublished !== true) return 'legal';
  return 'finish';
}

// ── The wizard → Studio hand-off ─────────────────────────────────────────────────────────────
// The Design step's "Build my site" opens the Studio with the description as ?prompt=. That
// prompt reads like any other "build me a site", which now sends the agent back to the wizard;
// the marker tells the Studio this one came FROM the wizard, so it builds instead of looping.

const HANDOFF_KEY = 'bls.setupWizardHandoff.v1';

// sessionStorage is per tab, so another wizard tab cannot pick this up; it is also cleared on an
// account switch (store/authStore ACCOUNT_SCOPED_SESSION_KEYS). It is tied to the site the wizard
// built, so a site switch before the Studio reads it sends the agent back to the wizard instead.

export interface WizardHandoff {
  /** The site already has content and the agent confirmed replacing it in the wizard. */
  replace: boolean;
}

interface SavedHandoff extends WizardHandoff {
  prompt: string;
  siteId: string | null;
}

/** Blocked storage (some private windows) is safe, since the agent lands back in the wizard
 *  rather than in a build. Logged so that loop is visible in the console if it happens. */
function storageFailed(what: string, err: unknown): void {
  console.warn(`[setupWizardRoute] could not ${what} the wizard hand-off`, err);
}

export function markWizardHandoff(prompt: string, handoff: WizardHandoff & { siteId: string | null }): void {
  try {
    const saved: SavedHandoff = { prompt: prompt.trim(), replace: handoff.replace, siteId: handoff.siteId };
    sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(saved));
  } catch (err) {
    storageFailed('save', err);
  }
}

function readHandoff(prompt: string, siteId: string | null): SavedHandoff | null {
  try {
    const raw = sessionStorage.getItem(HANDOFF_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw) as Partial<SavedHandoff>;
    if (saved.prompt !== prompt.trim()) return null;
    // A hand-off with no site (an account whose site was not known yet) matches any site.
    if (saved.siteId && saved.siteId !== siteId) return null;
    return { prompt: saved.prompt, replace: saved.replace === true, siteId: saved.siteId ?? null };
  } catch (err) {
    storageFailed('read', err);
    return null;
  }
}

/** Whether this prompt is the wizard's hand-off for this site, without consuming it — so the
 *  Studio chat sends it straight to the build rather than through the AI router, which may not
 *  read a plain description ("A modern coastal site for …") as a build at all. */
export function isWizardHandoff(prompt: string, siteId: string | null): boolean {
  return readHandoff(prompt, siteId) !== null;
}

/** The hand-off for this prompt and site, consumed. null when it did not come from the wizard. */
export function takeWizardHandoff(prompt: string, siteId: string | null): WizardHandoff | null {
  const saved = readHandoff(prompt, siteId);
  if (!saved) return null;
  try {
    sessionStorage.removeItem(HANDOFF_KEY);
  } catch (err) {
    storageFailed('clear', err);
  }
  return { replace: saved.replace };
}
